import { useState } from "react";
import { ApiError, type ApiClient } from "../../api/client";
import type { Authorization, ClockDay } from "../../api/types";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";

interface Props {
  api: ApiClient;
  /** Who gave their code for "In- of uitklokken". */
  authorization: Authorization;
  day: ClockDay;
  toast: (message: string, tone?: "info" | "bad") => void;
  onClose: () => void;
}

/** "Wednesday 1 October · 17:02" as the prototype shows it above the status. */
function today(now: Date): string {
  const date = now.toLocaleDateString("nl-BE", { weekday: "long", day: "numeric", month: "long" });
  return `${date} · ${formatTime(now)}`;
}

/**
 * The Klok window of the prototype: the person's status today and one button, Inklokken or
 * Uitklokken. After the one in and the one out of the day there is nothing left to do here.
 */
export function ClockDialog({ api, authorization, day, toast, onClose }: Props) {
  const text = t.time;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const done = day.status === "done";
  const action = day.status === "in" ? "out" : "in";

  const confirm = async () => {
    setBusy(true);
    try {
      const answer = await api.request<{ day: ClockDay }>("POST", "/device/time/clock", {
        staffToken: authorization.token,
        body: { action },
      });
      const at = formatTime(answer.day.clockOut ?? answer.day.clockIn ?? new Date());
      toast(action === "in" ? text.clockedIn(day.name, at) : text.clockedOut(day.name, at));
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={day.name}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {done ? text.close : text.cancel}
          </button>
          {!done && (
            <button
              type="button"
              className={`btn ${action === "out" ? "danger" : "primary"}`}
              disabled={busy}
              onClick={() => void confirm()}
            >
              {action === "out" ? text.clockOut : text.clockIn}
            </button>
          )}
        </>
      }
    >
      <div className="clockbody">
        <div className="note">{today(new Date())}</div>
        <p className="clockstatus">
          {day.status === "in" && day.clockIn !== null && (
            <span className="stp on">{text.status.in(formatTime(day.clockIn))}</span>
          )}
          {day.status === "done" && day.clockIn !== null && day.clockOut !== null && (
            <span className="stp off">{text.status.done(formatTime(day.clockIn), formatTime(day.clockOut))}</span>
          )}
          {day.status === "out" && <span className="stp off">{text.status.out}</span>}
        </p>
        {done && <p className="note">{text.once}</p>}
        <p className="pinmsg" role="alert">
          {error}
        </p>
      </div>
    </Dialog>
  );
}
